/**
 * Tykables 7.0 - Popup Controller
 *
 * Reads the JSON config emitted by sections/popup-manager.liquid, filters
 * popups by geo + page + frequency, queues matches, and surfaces them one
 * at a time after a minimum page delay.
 *
 * Hard rules baked in:
 *   - Never shows a popup on /checkout*
 *   - Never shows a popup on /password (pre-launch) unless the individual
 *     popup has "Pre-launch page" checked in its own block settings
 *   - Never shows two popups at once (queue)
 *   - Waits at least window.PopupManager.MIN_DELAY_MS per fresh page load
 *   - Respects prefers-reduced-motion (kills animation classes but still shows)
 *
 * Emits Shopify Web Pixel custom events on show/click/dismiss:
 *   - popup_shown    { popup_id, popup_name, position, trigger }
 *   - popup_clicked  { popup_id, popup_name, cta_url }
 *   - popup_dismissed{ popup_id, popup_name, method: 'close'|'escape'|'overlay' }
 *
 * Public API:
 *   window.PopupManager.trigger(id)   - force-show by popup id (bypasses filter)
 *   window.PopupManager.dismiss(id)   - hide + record frequency
 *   window.PopupManager.reset()       - clear all frequency records (testing)
 *   window.PopupManager.list()        - list config, eligibility, frequency state
 *
 * URL params for debug:
 *   ?popup_debug=1     - console logs every decision
 *   ?popup_force=<id>  - force this popup regardless of filters
 */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'tyk_popup:';
  var CONFIG_EL_ID = 'popup-manager-config';
  var RUNTIME_EL_ID = 'popup-manager-runtime';

  var urlParams = new URLSearchParams(window.location.search);
  var forceDebug = urlParams.get('popup_debug') === '1';
  var forceId = urlParams.get('popup_force');
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function log() {
    if (!forceDebug && (!runtime || !runtime.debug)) return;
    try { console.log.apply(console, ['[PopupManager]'].concat(Array.prototype.slice.call(arguments))); } catch (_) {}
  }

  function readJson(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (_) { return null; }
  }

  // ------------------------------------------------------------------
  // Load config
  // ------------------------------------------------------------------
  var configs = readJson(CONFIG_EL_ID) || [];
  var runtime = readJson(RUNTIME_EL_ID) || {};
  var MIN_DELAY_MS = runtime.min_delay_ms || 3000;

  // ------------------------------------------------------------------
  // Page & country detection
  // ------------------------------------------------------------------
  function detectCountry() {
    if (window.Shopify && window.Shopify.country) return String(window.Shopify.country).toUpperCase();
    if (runtime && runtime.country) return String(runtime.country).toUpperCase();
    return null;
  }

  function detectPageType() {
    // Prefer Liquid-supplied template name, fall back to URL pattern detection.
    var t = (runtime && runtime.template_name) || '';
    if (t) return normalizeTemplate(t);
    var p = window.location.pathname;
    if (p === '/' || p === '') return 'home';
    if (/^\/products\//.test(p)) return 'product';
    if (/^\/collections\/[^/]+\/products\//.test(p)) return 'product';
    if (/^\/collections/.test(p)) return 'collection';
    if (p === '/cart' || /^\/cart(\?|\/|$)/.test(p)) return 'cart';
    if (/^\/blogs\//.test(p)) return 'blog';
    if (/^\/search/.test(p)) return 'search';
    if (/^\/account/.test(p)) return 'account';
    if (/^\/password/.test(p)) return 'password';
    if (/^\/pages\//.test(p)) return 'page';
    return 'page';
  }

  function normalizeTemplate(name) {
    name = String(name || '').toLowerCase();
    if (name === 'index') return 'home';
    if (name === 'product') return 'product';
    if (name === 'collection') return 'collection';
    if (name === 'list-collections') return 'collection';
    if (name === 'cart') return 'cart';
    if (name === 'blog' || name === 'article') return 'blog';
    if (name === 'search') return 'search';
    if (name === 'password') return 'password';
    if (name === '404') return 'page';
    if (name.indexOf('customers') === 0) return 'account';
    if (name === 'page') return 'page';
    return name;
  }

  var country = detectCountry();
  var pageType = detectPageType();
  var pathname = (runtime && runtime.path) || window.location.pathname;

  log('boot', { country: country, pageType: pageType, configs: configs.length, reducedMotion: reducedMotion });

  // ------------------------------------------------------------------
  // Hard blocks
  // ------------------------------------------------------------------
  if (runtime && runtime.kill_switch) {
    log('kill switch on; exiting');
    return;
  }
  if (/^\/checkout/.test(pathname)) {
    log('on checkout path; exiting');
    return;
  }

  // ------------------------------------------------------------------
  // Frequency storage
  // ------------------------------------------------------------------
  function freqKey(id, rule) { return STORAGE_PREFIX + id + ':' + rule; }

  function isSuppressed(cfg) {
    if (cfg.frequency === 'always') return false;
    if (cfg.frequency === 'session') {
      try {
        return sessionStorage.getItem(freqKey(cfg.id, 'session')) === '1';
      } catch (_) { return false; }
    }
    try {
      var last = parseInt(localStorage.getItem(freqKey(cfg.id, cfg.frequency)) || '0', 10);
      if (!last) return false;
      var now = Date.now();
      var windowMs = cfg.frequency === '7_days' ? 7 * 86400e3
                   : cfg.frequency === '30_days' ? 30 * 86400e3
                   : 0;
      return (now - last) < windowMs;
    } catch (_) { return false; }
  }

  function markShown(cfg) {
    try {
      if (cfg.frequency === 'session') {
        sessionStorage.setItem(freqKey(cfg.id, 'session'), '1');
      } else if (cfg.frequency !== 'always') {
        localStorage.setItem(freqKey(cfg.id, cfg.frequency), String(Date.now()));
      }
    } catch (_) {}
  }

  // ------------------------------------------------------------------
  // Eligibility filter
  // ------------------------------------------------------------------
  function matchesCountry(cfg) {
    var spec = String(cfg.countries || 'all').trim().toLowerCase();
    if (!spec || spec === 'all' || spec === '*') return true;
    if (!country) return true; // no country info — let it pass
    var list = spec.split(',').map(function (s) { return s.trim().toUpperCase(); }).filter(Boolean);
    return list.indexOf(country) !== -1;
  }

  function matchesPage(cfg) {
    if (!cfg.pages) return false;
    // Special: if every page toggle is false, the popup is effectively disabled.
    var any = false;
    for (var k in cfg.pages) { if (cfg.pages[k]) { any = true; break; } }
    if (!any) return false;
    return !!cfg.pages[pageType];
  }

  function isEligible(cfg) {
    if (!cfg.enabled) return { ok: false, reason: 'disabled' };
    if (!matchesCountry(cfg)) return { ok: false, reason: 'country' };
    if (!matchesPage(cfg)) return { ok: false, reason: 'page' };
    if (runtime && runtime.debug) return { ok: true, reason: 'debug-bypass-frequency' };
    if (isSuppressed(cfg)) return { ok: false, reason: 'frequency' };
    return { ok: true };
  }

  // ------------------------------------------------------------------
  // Queue + show
  // ------------------------------------------------------------------
  var activePopupId = null;
  var shownThisLoad = {};

  function findCfg(id) {
    for (var i = 0; i < configs.length; i++) if (configs[i].id === id) return configs[i];
    return null;
  }

  function findEl(id) { return document.getElementById(id); }

  function showPopup(cfg, meta) {
    if (activePopupId) return; // queue rule: one at a time
    if (shownThisLoad[cfg.id]) return;
    var el = findEl(cfg.dom_id);
    if (!el) { log('no element for', cfg.id); return; }
    shownThisLoad[cfg.id] = true;
    activePopupId = cfg.id;

    el.hidden = false;
    el.setAttribute('aria-hidden', 'false');
    if (reducedMotion) el.classList.add('popup--no-motion');
    // Force reflow so the animation runs
    void el.offsetWidth;
    el.classList.add('is-open');

    // Focus management for center modals
    if (cfg.position === 'center') {
      el.setAttribute('aria-modal', 'true');
      trapFocus(el);
      var first = el.querySelector('[data-popup-close], .popup__cta, h2');
      if (first && typeof first.focus === 'function') first.focus({ preventScroll: true });
    }

    markShown(cfg);
    emit('popup_shown', { popup_id: cfg.id, popup_name: cfg.name, position: cfg.position, trigger: (meta && meta.trigger) || cfg.trigger_type });
    log('showing', cfg.id, meta);
  }

  function dismissPopup(id, method) {
    id = id || activePopupId;
    if (!id) return;
    var el = findEl('popup-' + String(id).replace(/^popup-/, '')); // tolerate either form
    var cfg = findCfg(id);
    if (!el && cfg) el = findEl(cfg.dom_id);
    if (!el) return;

    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    releaseFocusTrap(el);

    // Wait for exit transition so users see the motion before DOM hide
    var finish = function () {
      el.hidden = true;
      if (activePopupId === id) activePopupId = null;
    };
    if (reducedMotion) finish();
    else setTimeout(finish, 240);

    emit('popup_dismissed', { popup_id: id, popup_name: cfg ? cfg.name : null, method: method || 'close' });
    log('dismissed', id, method);
  }

  // ------------------------------------------------------------------
  // Trigger wiring
  // ------------------------------------------------------------------
  var eligibleQueue = [];

  function schedulePopup(cfg) {
    eligibleQueue.push(cfg);

    if (cfg.trigger_type === 'immediate') {
      setTimeout(function () { if (!activePopupId) showPopup(cfg, { trigger: 'immediate' }); }, MIN_DELAY_MS);
    } else if (cfg.trigger_type === 'delay') {
      var ms = Math.max(MIN_DELAY_MS, parseInt(cfg.trigger_value, 10) * 1000 || 5000);
      setTimeout(function () { if (!activePopupId) showPopup(cfg, { trigger: 'delay' }); }, ms);
    } else if (cfg.trigger_type === 'scroll_percent') {
      var pct = Math.max(0, Math.min(100, parseInt(cfg.trigger_value, 10) || 50));
      var bootTs = Date.now();
      var onScroll = function () {
        if (activePopupId) return;
        if ((Date.now() - bootTs) < MIN_DELAY_MS) return;
        var scrolled = (window.scrollY || window.pageYOffset || 0) + window.innerHeight;
        var total = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
        if (total <= 0) return;
        var reached = (scrolled / total) * 100;
        if (reached >= pct) {
          window.removeEventListener('scroll', onScroll);
          showPopup(cfg, { trigger: 'scroll_percent' });
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    } else if (cfg.trigger_type === 'exit_intent') {
      bindExitIntent(cfg);
    }
  }

  var exitIntentBound = false;
  var exitIntentPopups = [];
  function bindExitIntent(cfg) {
    exitIntentPopups.push(cfg);
    if (exitIntentBound) return;
    exitIntentBound = true;
    var bootTs = Date.now();

    // Desktop: cursor leaves viewport from the top
    document.addEventListener('mouseleave', function (e) {
      if (activePopupId) return;
      if ((Date.now() - bootTs) < MIN_DELAY_MS) return;
      if (e.clientY > 0) return;
      fireExit();
    });

    // Mobile fallback: fast upward scroll after user has scrolled down
    var lastY = window.scrollY || 0;
    var lastT = Date.now();
    window.addEventListener('scroll', function () {
      if (activePopupId) return;
      if ((Date.now() - bootTs) < MIN_DELAY_MS) return;
      var y = window.scrollY || 0;
      var t = Date.now();
      var dy = lastY - y;  // positive when scrolling up
      var dt = Math.max(1, t - lastT);
      var speed = dy / dt; // px per ms
      lastY = y;
      lastT = t;
      if (y < 100 && speed > 2.0 && lastY > 200) fireExit();
    }, { passive: true });

    function fireExit() {
      var next = exitIntentPopups.shift();
      if (next) showPopup(next, { trigger: 'exit_intent' });
    }
  }

  // ------------------------------------------------------------------
  // Analytics (Shopify Web Pixel)
  // ------------------------------------------------------------------
  function emit(eventName, payload) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, payload || {});
      }
    } catch (_) {}
    log('event', eventName, payload);
  }

  // ------------------------------------------------------------------
  // Focus trap for center modals
  // ------------------------------------------------------------------
  var lastFocused = null;
  var trappedEl = null;
  var trapHandler = null;

  function trapFocus(el) {
    lastFocused = document.activeElement;
    trappedEl = el;
    trapHandler = function (e) {
      if (e.key !== 'Tab') return;
      var focusables = el.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', trapHandler);
  }

  function releaseFocusTrap(el) {
    if (trappedEl === el) {
      document.removeEventListener('keydown', trapHandler);
      trapHandler = null;
      trappedEl = null;
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus({ preventScroll: true }); } catch (_) {}
      }
      lastFocused = null;
    }
  }

  // ------------------------------------------------------------------
  // Event listeners (close button, CTA, Escape key)
  // ------------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var close = e.target.closest && e.target.closest('[data-popup-close]');
    if (close) {
      e.preventDefault();
      var host = close.closest('[data-popup]');
      if (host) dismissPopup(host.getAttribute('data-popup-id'), 'close');
      return;
    }
    var cta = e.target.closest && e.target.closest('[data-popup-cta]');
    if (cta) {
      var hostCta = cta.closest('[data-popup]');
      if (hostCta) {
        var id = hostCta.getAttribute('data-popup-id');
        var cfg = findCfg(id);
        emit('popup_clicked', { popup_id: id, popup_name: cfg ? cfg.name : null, cta_url: cta.getAttribute('href') });
        dismissPopup(id, 'cta');
      }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && activePopupId) dismissPopup(activePopupId, 'escape');
  });

  // ------------------------------------------------------------------
  // Public API
  // ------------------------------------------------------------------
  window.PopupManager = {
    MIN_DELAY_MS: MIN_DELAY_MS,
    trigger: function (id) {
      var cfg = findCfg(id);
      if (!cfg) { log('trigger: no config for', id); return; }
      showPopup(cfg, { trigger: 'manual' });
    },
    dismiss: function (id) { dismissPopup(id, 'api'); },
    reset: function () {
      try {
        for (var k in localStorage) if (k.indexOf && k.indexOf(STORAGE_PREFIX) === 0) localStorage.removeItem(k);
        for (var k2 in sessionStorage) if (k2.indexOf && k2.indexOf(STORAGE_PREFIX) === 0) sessionStorage.removeItem(k2);
      } catch (_) {}
      log('frequency reset');
    },
    list: function () {
      return configs.map(function (c) {
        return { id: c.id, name: c.name, enabled: c.enabled, eligibility: isEligible(c) };
      });
    }
  };

  // ------------------------------------------------------------------
  // Bootstrap: filter & schedule
  // ------------------------------------------------------------------
  function init() {
    if (forceId) {
      log('force mode for', forceId);
      var f = findCfg(forceId);
      if (f) setTimeout(function () { showPopup(f, { trigger: 'force' }); }, 100);
      return;
    }

    configs.forEach(function (cfg) {
      var verdict = isEligible(cfg);
      if (!verdict.ok) { log('skip', cfg.id, verdict.reason); return; }
      schedulePopup(cfg);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
